import React from 'react';
import { Dialog } from 'primereact/dialog';
import { Card } from 'primereact/card';
import { Tag } from 'primereact/tag';
import { Divider } from 'primereact/divider';
import { Panel } from 'primereact/panel';
import { Image } from 'primereact/image';
import { Chip } from 'primereact/chip';
import { Badge } from 'primereact/badge';
import { Button } from 'primereact/button';
import { Accordion, AccordionTab } from 'primereact/accordion';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';

interface ProductInfoDialogProps {
    visible: boolean;
    onHide: () => void;
    product: any;
    onEdit?: () => void;
    onDelete?: () => void;
}

const ProductInfoDialog: React.FC<ProductInfoDialogProps> = ({ visible, onHide, product, onEdit, onDelete }) => {
    const dialogHeader = (
        <div className="flex align-items-center gap-3">
            <i className="pi pi-info-circle text-2xl text-primary"></i>
            <span className="text-xl font-bold text-primary">Product Information</span>
        </div>
    );

    const dialogFooter = (
        <div className="flex justify-content-end gap-2">
            {onDelete && (
                <Button
                    label="Delete Product"
                    icon="pi pi-trash"
                    severity="danger"
                    outlined
                    onClick={onDelete}
                    type="button"
                />
            )}
            {onEdit && (
                <Button
                    label="Edit Product"
                    icon="pi pi-pencil"
                    severity="info"
                    onClick={onEdit}
                    type="button"
                />
            )}
            <Button label="Close" icon="pi pi-times" severity="secondary" outlined onClick={onHide} type="button" />
        </div>
    );

    const getStockSeverity = (inStock: boolean) => {
        return inStock ? 'success' : 'danger';
    };

    const getStockIcon = (inStock: boolean) => {
        return inStock ? 'pi pi-check-circle' : 'pi pi-times-circle';
    };

    const getStatusSeverity = (status: string) => {
        switch (status?.toLowerCase()) {
            case 'approved': return 'success';
            case 'rejected': return 'danger';
            case 'pending': return 'warning';
            default: return 'info';
        }
    };

    const pricingData = product ? [
        { label: 'Base Price (Excl. GST)', value: `₹${product.price}`, icon: 'pi pi-money-bill' },
        { label: 'GST Rate', value: `${product.gst}%`, icon: 'pi pi-percentage' },
        { label: 'Final Price (Incl. GST)', value: `₹${product.withGstprice}`, icon: 'pi pi-tag' }
    ] : [];

    const pricingTemplate = (data: any) => {
        return (
            <div className="flex align-items-center justify-content-between p-2">
                <div className="flex align-items-center gap-2">
                    <i className={`${data.icon} text-primary`}></i>
                    <span className="font-medium">{data.label}</span>
                </div>
                <Tag value={data.value} severity="info" className="font-bold" />
            </div>
        );
    };

    return (
        <Dialog
            header={dialogHeader}
            visible={visible}
            onHide={onHide}
            style={{ width: '60vw', minWidth: '800px' }}
            className="product-info-dialog"
            maximizable
            modal
            footer={dialogFooter}
        >
            {product ? (
                <div className="flex flex-column gap-4">
                    {/* Product Overview Card */}
                    <Card className="shadow-3 surface-ground">
                        <div className="grid">
                            <div className="col-12 md:col-4">
                                <div className="text-center">
                                    <Image
                                        src={product.image}
                                        alt={product.name}
                                        width="250"
                                        height="250"
                                        className="border-round-lg shadow-4"
                                        onError={(e: any) => e.target.src = '/image.png'}
                                        preview
                                    />
                                </div>
                            </div>
                            
                            <div className="col-12 md:col-8">
                                <div className="flex flex-column gap-3 h-full">
                                    <div>
                                        <h2 className="text-2xl font-bold text-900 mt-0 mb-2">{product.name}</h2>
                                        <div className="flex gap-2 mb-3">
                                            <Chip 
                                                label={product.category} 
                                                icon="pi pi-tag" 
                                                className="bg-blue-100 text-blue-800"
                                            />
                                            <Tag 
                                                value={product.status} 
                                                severity={getStatusSeverity(product.status)}
                                                icon="pi pi-circle-fill"
                                            />
                                            <Tag 
                                                value={product.isInstock ? "In Stock" : "Out of Stock"} 
                                                severity={getStockSeverity(product.isInstock)}
                                                icon={getStockIcon(product.isInstock)}
                                            />
                                        </div>
                                    </div>
                                    
                                    <div className="flex-1">
                                        <p className="text-700 line-height-3 mb-3">{product.description}</p>
                                        
                                        <div className="grid">
                                            <div className="col-6">
                                                <div className="bg-primary-50 p-3 border-round text-center">
                                                    <div className="text-2xl font-bold text-primary">
                                                        {product.quantity} {product.unit}
                                                    </div>
                                                    <div className="text-600 text-sm">Available Quantity</div>
                                                </div>
                                            </div>
                                            <div className="col-6">
                                                <div className="bg-green-50 p-3 border-round text-center">
                                                    <div className="text-2xl font-bold text-green-600">
                                                        ₹{product.withGstprice}
                                                    </div>
                                                    <div className="text-600 text-sm">Price (Incl. GST)</div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Card>

                    {/* Detailed Information Accordion */}
                    <Accordion className="shadow-2">
                        <AccordionTab header="💰 Pricing Details">
                            <DataTable 
                                value={pricingData} 
                                className="p-datatable-sm"
                                showGridlines={false}
                            >
                                <Column body={pricingTemplate} />
                            </DataTable>
                            
                            <Divider className="my-3" />
                            
                            <div className="bg-yellow-50 p-3 border-round">
                                <div className="flex align-items-center gap-2 mb-2">
                                    <i className="pi pi-info-circle text-yellow-600"></i>
                                    <span className="font-semibold text-yellow-800">HSN Code Information</span>
                                </div>
                                <p className="m-0 text-700">
                                    HSN Code: <strong>{product.hsnCode}</strong>
                                </p>
                                <p className="m-0 text-600 text-sm mt-1">
                                    Harmonized System of Nomenclature code for tax classification
                                </p>
                            </div>
                        </AccordionTab>

                        <AccordionTab header="🥗 Nutrition Information">
                            <Panel className="bg-green-50">
                                <div className="flex align-items-center gap-2 mb-3">
                                    <i className="pi pi-heart text-green-600 text-xl"></i>
                                    <span className="font-semibold text-green-800">Nutritional Facts</span>
                                </div>
                                <div className="white-space-pre-line text-700 line-height-3">
                                    {product.nutritionInfo || 'No nutrition information available'}
                                </div>
                            </Panel>
                        </AccordionTab>

                        <AccordionTab header="📊 Product Specifications">
                            <div className="grid">
                                <div className="col-12 md:col-6">
                                    <Card className="h-full bg-blue-50">
                                        <h4 className="text-blue-800 mt-0 mb-3">
                                            <i className="pi pi-cog mr-2"></i>
                                            Technical Details
                                        </h4>
                                        <div className="flex flex-column gap-2">
                                            <div className="flex justify-content-between">
                                                <span className="text-600">Category:</span>
                                                <span className="font-semibold">{product.category}</span>
                                            </div>
                                            <div className="flex justify-content-between">
                                                <span className="text-600">Unit:</span>
                                                <span className="font-semibold">{product.unit}</span>
                                            </div>
                                            <div className="flex justify-content-between">
                                                <span className="text-600">HSN Code:</span>
                                                <span className="font-semibold">{product.hsnCode}</span>
                                            </div>
                                        </div>
                                    </Card>
                                </div>
                                
                                <div className="col-12 md:col-6">
                                    <Card className="h-full bg-orange-50">
                                        <h4 className="text-orange-800 mt-0 mb-3">
                                            <i className="pi pi-warehouse mr-2"></i>
                                            Inventory Status
                                        </h4>
                                        <div className="flex flex-column gap-2">
                                            <div className="flex justify-content-between">
                                                <span className="text-600">Stock Status:</span>
                                                <Tag 
                                                    value={product.isInstock ? "Available" : "Out of Stock"} 
                                                    severity={getStockSeverity(product.isInstock)}
                                                />
                                            </div>
                                            <div className="flex justify-content-between">
                                                <span className="text-600">Quantity:</span>
                                                <Badge 
                                                    value={`${product.quantity} ${product.unit}`} 
                                                    severity="info"
                                                />
                                            </div>
                                            <div className="flex justify-content-between">
                                                <span className="text-600">Status:</span>
                                                <Tag 
                                                    value={product.status} 
                                                    severity={getStatusSeverity(product.status)}
                                                />
                                            </div>
                                        </div>
                                    </Card>
                                </div>
                            </div>
                        </AccordionTab>
                    </Accordion>

                </div>
            ) : (
                <div className="text-center py-8">
                    <i className="pi pi-exclamation-triangle text-6xl text-400 mb-3"></i>
                    <p className="text-600 text-xl">Product information not available</p>
                </div>
            )}
        </Dialog>
    );
};

export default ProductInfoDialog;
